import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AdminMfaModal from '../../components/AdminMfaModal';
import {
  Plus,
  Search,
  User,
  Mail,
  Shield,
  Edit,
  Trash2,
  MoreVertical,
  Key,
  CheckCircle,
  XCircle,
  ShieldCheck,
  ShieldOff
} from 'lucide-react';
import toast from 'react-hot-toast';

const roleColors = {
  super_admin: 'bg-red-100 text-red-700',
  admin: 'bg-purple-100 text-purple-700',
  support: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-700'
};

const AdminUsersPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [mfaUser, setMfaUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'admin',
    mobile: '',
    password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAdminUsers();
      setUsers(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load admin users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        mobile: user.mobile || '',
        password: ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'admin',
        mobile: '',
        password: ''
      });
    }
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await adminApi.updateAdminUser(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        await adminApi.createAdminUser(formData);
        toast.success('User created successfully');
      }
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      toast.error(editingUser ? 'Failed to update user' : 'Failed to create user');
      console.error(err);
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user.name}?`)) return;

    try {
      await adminApi.deleteAdminUser(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
      console.error(err);
    }
  };

  const handleResetPassword = async (user) => {
    if (!confirm(`Send password reset email to ${user.email}?`)) return;

    try {
      await adminApi.resetAdminPassword(user.id);
      toast.success('Password reset email sent');
    } catch (err) {
      toast.error('Failed to send password reset');
      console.error(err);
    }
  };

  const handleResetMfa = async (user) => {
    if (!confirm(`Reset MFA for ${user.name}? They will sign in with their password only until they enrol again.`)) return;
    try {
      await adminApi.resetAdminUserMfa(user.id);
      toast.success('MFA reset');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset MFA');
      console.error(err);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await adminApi.toggleAdminUserStatus(user.id);
      toast.success(user.isActive ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
      console.error(err);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Users</h1>
          <p className="text-gray-500 mt-1">Manage administrator accounts</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Add Admin User
        </button>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header">User</th>
                <th className="table-header">Role</th>
                <th className="table-header">Status</th>
                <th className="table-header">MFA</th>
                <th className="table-header">Last Login</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    No admin users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          {user.mobile && <p className="text-xs text-gray-400">{user.mobile}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          roleColors[user.role] || roleColors.viewer
                        }`}
                      >
                        {user.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span
                        className={`inline-flex items-center ${
                          user.isActive ? 'text-green-600' : 'text-gray-400'
                        }`}
                      >
                        {user.isActive ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-1" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="table-cell">
                      {user.mfaEnabled ? (
                        <span className="badge-success">{user.mfaMethod === 'sms' ? 'SMS' : 'Authenticator'}</span>
                      ) : (
                        <span className="badge-gray">Disabled</span>
                      )}
                    </td>
                    <td className="table-cell text-gray-500">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        {me && String(me.id) === String(user.id) ? (
                          <button
                            onClick={() => setMfaUser(user)}
                            className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50"
                            title="Multi-factor authentication"
                          >
                            <ShieldCheck className="w-4 h-4" />
                          </button>
                        ) : user.mfaEnabled ? (
                          <button
                            onClick={() => handleResetMfa(user)}
                            className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-amber-50"
                            title="Reset MFA (lost phone)"
                          >
                            <ShieldOff className="w-4 h-4" />
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(user)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          className={`p-2 rounded-lg ${
                            user.isActive
                              ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                          }`}
                          title={user.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {user.isActive ? (
                            <XCircle className="w-4 h-4" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Descriptions */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Role Permissions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors.super_admin}`}>
                Super Admin
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Full access to all features including user management and system settings
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors.admin}`}>
                Admin
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Full access to tenants, subscriptions, and support without system settings
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors.support}`}>
                Support
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Access to support tickets and view-only access to tenant information
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors.viewer}`}>
                Viewer
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Read-only access to dashboards and analytics
            </p>
          </div>
        </div>
      </div>

      {mfaUser && (
        <AdminMfaModal
          user={mfaUser}
          onClose={() => setMfaUser(null)}
          onChanged={() => { setMfaUser(null); fetchUsers(); }}
        />
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Edit Admin User' : 'Add Admin User'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile number
                </label>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="input"
                  placeholder="0412 345 678"
                />
                <p className="text-xs text-gray-500 mt-1">Used for SMS sign-in codes when that MFA method is chosen.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input"
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="support">Support</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input"
                  required={!editingUser}
                  minLength={8}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
